import {
  UseFormRegister,
  FieldErrors,
  Control,
  Controller,
} from 'react-hook-form';
import { Label } from '@components/ui/Label';
import type { EnumsResponse } from '@/types';
import type { FormData } from './schema';
import { INPUT_CLS, TEXTAREA_CLS } from './constants';
import { ProjectTypeSelect } from './ProjectTypeSelect';
import { Button } from '@/components/ui/Button';

interface Step1Props {
  register: UseFormRegister<FormData>;
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  summaryLength: number;
  enums: EnumsResponse | undefined;
  onNext: () => void;
}

export function Step1({
  register,
  control,
  errors,
  summaryLength,
  enums,
  onNext,
}: Step1Props) {
  return (
    <>
      <h1 className="mb-8 text-3xl font-bold text-[--text-dark-primary]">
        Let&apos;s start with your project description
      </h1>

      <div className="formContainer createProjectForm">
        <div className="space-y-[--spacing-14]">
          {/* Row 1: Title + Project Type */}
          <div className="grid grid-cols-1 gap-[--spacing-14] lg:grid-cols-2">
            {/* Left column */}
            <div className="space-y-[--spacing-14]">
              {/* Title */}
              <div className="inputGroup">
                <Label htmlFor="title">Project Title</Label>
                <input
                  id="title"
                  type="text"
                  placeholder="Enter your project title"
                  className={`${INPUT_CLS} h-11 !text-base`}
                  {...register('title')}
                />
                {errors.title?.message && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.title.message}
                  </p>
                )}
              </div>

              {/* Short Summary */}
              <div className="inputGroup">
                <Label htmlFor="summary">Short Summary</Label>
                <textarea
                  id="summary"
                  placeholder="Brief summary of your project"
                  className={`${TEXTAREA_CLS} !min-h-[72px] !rounded-[--radi-6] !text-base`}
                  {...register('summary')}
                />
                <div className="helperText">
                  {errors.summary?.message ? (
                    <p className="text-red-400">{errors.summary.message}</p>
                  ) : (
                    <span>Max. 280 characters</span>
                  )}
                  <span
                    className={`${summaryLength > 280 ? 'text-red-400' : ''}`}
                  >
                    {summaryLength}/280
                  </span>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-[--spacing-14]">
              {/* Project Type */}
              <div className="inputGroup">
                <Label id="projectTypeLabel" htmlFor="projectType">
                  Project Type
                </Label>
                <Controller
                  control={control}
                  name="projectType"
                  render={({ field }) => (
                    <ProjectTypeSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={enums?.projectTypes ?? []}
                    />
                  )}
                />
                {errors.projectType?.message && (
                  <p className="helperText text-sm text-red-400">
                    {errors.projectType.message}
                  </p>
                )}
              </div>

              {/* Project Link */}
              <div className="inputGroup">
                <Label htmlFor="url">Project Link</Label>
                <div className="relative">
                  <i className="ri-global-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-[rgba(255,255,255,0.4)]" />
                  <input
                    id="url"
                    type="text"
                    placeholder="www.example.com"
                    className={`${INPUT_CLS} h-11 !pl-12 !text-base`}
                    {...register('url')}
                  />
                </div>
                {errors.url?.message && (
                  <p className="helperText text-sm text-red-400">
                    {errors.url.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Full-width: Description */}
          <div className="inputGroup">
            <Label htmlFor="description">Project Description</Label>
            <textarea
              id="description"
              placeholder="Provide a detailed description of your project requirements"
              className={`${TEXTAREA_CLS} !min-h-[180px] !rounded-[--radi-6] !text-base`}
              {...register('description')}
            />
            {errors.description?.message && (
              <p className="helperText text-sm !text-red-400">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Next button */}
          <div className="flex justify-end">
            <Button type="button" onClick={onNext}>
              Next: Define Objectives
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
